import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Num, T, useGT, Var } from 'gt-react';
import {
  ArrowElbowDownRightIcon,
  CaretDownIcon,
  CaretRightIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
  TreeStructureIcon,
} from '@phosphor-icons/react';
import {
  MAX_TASK_TYPE_STAGE_TREE_DEPTH,
  type GenerateTaskTypeStageTreeInput,
  type GenerateTaskTypeStageTreeResponse,
  type TaskTypeResponse,
} from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { toast } from 'src/components/ui/toast';
import { cn } from 'src/lib/utils';
import {
  useApplyTaskTypeStageTree,
  useGenerateTaskTypeStageTree,
} from 'src/api/rpc/hooks/tasks/useTaskTypes';
import {
  IntendedAssigneeSelect,
  NestedStageConnector,
  StageHierarchyBranch,
  StageReorderButtons,
} from './TaskTypePersistedStageTree';
import {
  addChildDraftNode,
  buildGenerateStageTreeInput,
  draftToStageTree,
  moveDraftNode,
  newStageDraftNode,
  removeDraftNode,
  stageDraftKeyAriaInvalid,
  stageNodeCardClassName,
  stageTreeToDraft,
  reusedExistingType,
  updateDraftNode,
  validateStageDraft,
  type StageDraftIssue,
  type StageTreeDraftNode,
  type TaskTypeStageOption,
} from './taskTypeStageTree';
import type { TaskTypeEditorState } from './taskTypeAdmin';

function toastCaughtError(error: unknown, fallback: string) {
  toast.error(error instanceof Error ? error.message : fallback);
}

async function runStageTreeGeneration(opts: {
  prompt: string;
  disabled?: boolean;
  pending: boolean;
  generate: (
    input: GenerateTaskTypeStageTreeInput,
  ) => Promise<GenerateTaskTypeStageTreeResponse>;
  editor: TaskTypeEditorState;
  stageOptions: TaskTypeStageOption[];
  fallbackError: string;
}): Promise<{
  draft: StageTreeDraftNode[];
  explanation: string | null;
} | null> {
  const trimmed = opts.prompt.trim();
  if (!trimmed || opts.pending || opts.disabled) return null;
  try {
    const result = await opts.generate(
      buildGenerateStageTreeInput({
        prompt: trimmed,
        parent: {
          key: opts.editor.key,
          name: opts.editor.name.trim() || opts.editor.key,
          description: opts.editor.description.trim() || null,
        },
        stageOptions: opts.stageOptions,
        expectedSubtasks: opts.editor.expectedSubtasks,
      }),
    );
    return {
      draft: stageTreeToDraft(result.stages),
      explanation: result.explanation || null,
    };
  } catch (error) {
    toastCaughtError(error, opts.fallbackError);
    return null;
  }
}

export function TaskTypeGenerateStagesPanel({
  editor,
  stageOptions,
  disabled,
  defaultOpen,
  onApplied,
}: {
  editor: TaskTypeEditorState;
  stageOptions: TaskTypeStageOption[];
  disabled?: boolean;
  defaultOpen?: boolean;
  onApplied: (saved: TaskTypeResponse) => void;
}) {
  const gt = useGT();
  const generate = useGenerateTaskTypeStageTree();
  const apply = useApplyTaskTypeStageTree();
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [prompt, setPrompt] = useState('');
  const [explanation, setExplanation] = useState<string | null>(null);
  const [draft, setDraft] = useState<StageTreeDraftNode[] | null>(null);

  const parentId = editor.editing?.id;
  const issues = draft
    ? validateStageDraft({ nodes: draft, parentKey: editor.key })
    : [];
  const canApply =
    Boolean(parentId && draft && draft.length > 0 && issues.length === 0) &&
    !apply.isPending &&
    !disabled;

  async function handleGenerate() {
    const result = await runStageTreeGeneration({
      prompt,
      disabled,
      pending: generate.isPending,
      generate: (input) => generate.mutateAsync(input),
      editor,
      stageOptions,
      fallbackError: gt('Failed to generate stages', {
        context: 'toast after AI stage tree generation failed',
      }),
    });
    if (!result) return;
    setDraft(result.draft);
    setExplanation(result.explanation);
    setOpen(true);
  }

  async function handleApply() {
    if (!parentId || !draft || issues.length > 0) return;
    try {
      const saved = await apply.mutateAsync({
        parentId,
        stages: draftToStageTree(draft),
      });
      onApplied(saved);
      setDraft(null);
      setExplanation(null);
      toast.success(
        gt(
          'Stages created. Review the tree, then save if you have other edits.',
          {
            context: 'toast after applying an AI-generated stage tree',
          },
        ),
      );
    } catch (error) {
      toastCaughtError(
        error,
        gt('Failed to apply stages', {
          context: 'toast after applying an AI stage tree failed',
        }),
      );
    }
  }

  return (
    <StageGenerationPanelView
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
      prompt={prompt}
      onPromptChange={setPrompt}
      generatePending={generate.isPending}
      applyPending={apply.isPending}
      explanation={explanation}
      draft={draft}
      stageOptions={stageOptions}
      parentKey={editor.key}
      issues={issues}
      canApply={canApply}
      onGenerate={() => void handleGenerate()}
      onApply={() => void handleApply()}
      onDraftChange={setDraft}
      onDiscard={() => {
        setDraft(null);
        setExplanation(null);
      }}
    />
  );
}

function StageGenerationPromptForm({
  prompt,
  disabled,
  generatePending,
  onPromptChange,
  onGenerate,
}: {
  prompt: string;
  disabled?: boolean;
  generatePending: boolean;
  onPromptChange: (prompt: string) => void;
  onGenerate: () => void;
}) {
  const gt = useGT();
  return (
    <>
      <p className="text-xs text-muted-foreground">
        <T context="hint for AI task type stage generation">
          Describe the process. AI drafts the whole checklist — including nested
          subtasks — and you can edit it before creating the types.
        </T>
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Textarea
          value={prompt}
          onChange={(event) => onPromptChange(event.target.value)}
          placeholder={gt(
            'e.g. Extract the invoice PDF, match PO lines, then a human approves…',
            { context: 'placeholder for AI task type stage generation' },
          )}
          rows={3}
          className="min-h-0 flex-1 resize-none text-sm"
          disabled={generatePending || disabled}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              onGenerate();
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          className="shrink-0"
          disabled={generatePending || disabled || !prompt.trim()}
          onClick={onGenerate}
        >
          {generatePending ? (
            <T context="AI generate stages loading label">Generating…</T>
          ) : (
            <T context="AI generate stages button">Generate</T>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        <T context="AI shortcut hint for generating task type stages">
          Cmd+Enter to generate. Applying creates any missing types and sets
          this type's checklist.
        </T>
      </p>
    </>
  );
}

function StageGenerationDraftResult({
  explanation,
  draft,
  stageOptions,
  parentKey,
  issues,
  disabled,
  applyPending,
  canApply,
  onDraftChange,
  onDiscard,
  onApply,
}: {
  explanation: string | null;
  draft: StageTreeDraftNode[] | null;
  stageOptions: TaskTypeStageOption[];
  parentKey: string;
  issues: StageDraftIssue[];
  disabled?: boolean;
  applyPending: boolean;
  canApply: boolean;
  onDraftChange: (next: StageTreeDraftNode[]) => void;
  onDiscard: () => void;
  onApply: () => void;
}) {
  return (
    <>
      {explanation ? (
        <p className="rounded-md border border-primary/15 bg-primary/5 px-2 py-1.5 text-xs text-muted-foreground">
          {explanation}
        </p>
      ) : null}
      {draft ? (
        <StageDraftEditor
          nodes={draft}
          stageOptions={stageOptions}
          parentKey={parentKey}
          issues={issues}
          disabled={disabled || applyPending}
          onChange={onDraftChange}
          onDiscard={onDiscard}
          onApply={onApply}
          canApply={canApply}
          isApplying={applyPending}
        />
      ) : null}
    </>
  );
}

function StageGenerationPanelView({
  disabled,
  open,
  onOpenChange,
  prompt,
  onPromptChange,
  generatePending,
  applyPending,
  explanation,
  draft,
  stageOptions,
  parentKey,
  issues,
  canApply,
  onGenerate,
  onApply,
  onDraftChange,
  onDiscard,
}: {
  disabled?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  generatePending: boolean;
  applyPending: boolean;
  explanation: string | null;
  draft: StageTreeDraftNode[] | null;
  stageOptions: TaskTypeStageOption[];
  parentKey: string;
  issues: StageDraftIssue[];
  canApply: boolean;
  onGenerate: () => void;
  onApply: () => void;
  onDraftChange: (next: StageTreeDraftNode[]) => void;
  onDiscard: () => void;
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-1 rounded-md border px-3 py-2 text-left text-sm font-medium hover:bg-muted/60',
          )}
          disabled={disabled}
        >
          {open ? (
            <CaretDownIcon className="size-4 shrink-0" />
          ) : (
            <CaretRightIcon className="size-4 shrink-0" />
          )}
          <SparkleIcon className="size-3.5 shrink-0" />
          <T context="collapsible trigger to generate task type stages with AI">
            Generate stages with AI
          </T>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-2 pt-2">
        <StageGenerationPromptForm
          prompt={prompt}
          disabled={disabled}
          generatePending={generatePending}
          onPromptChange={onPromptChange}
          onGenerate={onGenerate}
        />
        <StageGenerationDraftResult
          explanation={explanation}
          draft={draft}
          stageOptions={stageOptions}
          parentKey={parentKey}
          issues={issues}
          disabled={disabled}
          applyPending={applyPending}
          canApply={canApply}
          onDraftChange={onDraftChange}
          onDiscard={onDiscard}
          onApply={onApply}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

function StageDraftEditor({
  nodes,
  stageOptions,
  parentKey,
  issues,
  disabled,
  onChange,
  onDiscard,
  onApply,
  canApply,
  isApplying,
}: {
  nodes: StageTreeDraftNode[];
  stageOptions: readonly TaskTypeStageOption[];
  parentKey: string;
  issues: StageDraftIssue[];
  disabled?: boolean;
  onChange: (next: StageTreeDraftNode[]) => void;
  onDiscard: () => void;
  onApply: () => void;
  canApply: boolean;
  isApplying: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-2">
      <p className="text-xs font-medium">
        <T context="heading above the editable AI-generated stage tree">
          Review and edit
        </T>
      </p>
      <div className="flex flex-col gap-1.5">
        {nodes.map((node, index) => (
          <StageDraftRow
            key={node.clientId}
            node={node}
            index={index}
            siblingCount={nodes.length}
            depth={1}
            stageOptions={stageOptions}
            parentKey={parentKey}
            issues={issues}
            disabled={disabled}
            onChange={onChange}
            nodes={nodes}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled}
        onClick={() => onChange([...nodes, newStageDraftNode()])}
      >
        <PlusIcon className="size-3.5" />
        <T context="button adding a top-level stage to the AI draft">
          Add stage
        </T>
      </Button>
      {issues.length > 0 ? (
        <p className="text-xs text-destructive">
          <T context="validation summary for the AI-generated stage draft">
            Fix highlighted keys and names before applying.
          </T>
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onDiscard}
        >
          <T context="button discarding the AI-generated stage draft">
            Discard
          </T>
        </Button>
        <Button type="button" size="sm" disabled={!canApply} onClick={onApply}>
          {isApplying ? (
            <T context="button label while applying generated stages">
              Creating…
            </T>
          ) : (
            <T context="button applying the generated stage tree">
              Create stages
            </T>
          )}
        </Button>
      </div>
    </div>
  );
}

function AddSubtaskButton({
  disabled,
  onClick,
}: {
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="self-start"
      disabled={disabled}
      onClick={onClick}
    >
      <PlusIcon className="size-3.5" />
      <T context="button adding a nested subtask to a generated stage">
        Add subtask
      </T>
    </Button>
  );
}

function StageDraftReuseHint({ reused }: { reused: TaskTypeStageOption }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
      <span>
        <T context="explains that applying will attach an existing task type instead of creating a new one">
          This key already exists. Applying will attach <Var>{reused.name}</Var>{' '}
          instead of creating a new type, and will not change nested stages on
          that type.
        </T>
      </span>
      <Link
        to="/settings/organization/task-types/$taskTypeId"
        params={{ taskTypeId: reused.id }}
        className="font-medium text-foreground hover:underline"
      >
        <T context="link to open the existing task type that will be reused">
          Open type
        </T>
      </Link>
    </div>
  );
}

function StageDraftCard({
  node,
  index,
  siblingCount,
  issue,
  reused,
  disabled,
  isNested,
  onChange,
  nodes,
}: {
  node: StageTreeDraftNode;
  index: number;
  siblingCount: number;
  issue: StageDraftIssue | undefined;
  reused: TaskTypeStageOption | undefined;
  disabled?: boolean;
  isNested: boolean;
  onChange: (next: StageTreeDraftNode[]) => void;
  nodes: StageTreeDraftNode[];
}) {
  const gt = useGT();

  function patch(partial: Partial<Omit<StageTreeDraftNode, 'clientId'>>) {
    onChange(updateDraftNode(nodes, node.clientId, partial));
  }

  return (
    <div
      className={cn(
        stageNodeCardClassName(isNested),
        'flex flex-col gap-1.5 p-2',
      )}
    >
      <div className="flex items-start gap-2">
        {isNested ? (
          <ArrowElbowDownRightIcon
            aria-hidden
            className="mt-2 size-3.5 shrink-0 text-muted-foreground"
          />
        ) : null}
        <StageReorderButtons
          disabled={disabled}
          index={index}
          siblingCount={siblingCount}
          upLabel={gt('Move stage up', {
            context: 'reorder generated stage up',
          })}
          downLabel={gt('Move stage down', {
            context: 'reorder generated stage down',
          })}
          onMove={(direction) =>
            onChange(moveDraftNode(nodes, node.clientId, direction))
          }
        />
        <div className="grid min-w-0 flex-1 grid-cols-1 gap-1.5 sm:grid-cols-2">
          <Input
            value={node.name}
            disabled={disabled}
            placeholder={gt('Stage name', {
              context: 'placeholder for generated stage name',
            })}
            aria-invalid={issue?.message === 'name_required'}
            onChange={(event) => patch({ name: event.target.value })}
          />
          <Input
            value={node.key}
            disabled={disabled}
            placeholder={gt('type-key', {
              context: 'placeholder for generated stage key',
            })}
            className="font-mono text-xs"
            aria-invalid={stageDraftKeyAriaInvalid(issue?.message)}
            onChange={(event) => patch({ key: event.target.value })}
          />
        </div>
        {!isNested && node.children.length > 0 ? (
          <Badge
            variant="secondary"
            className="mt-0.5 h-5 shrink-0 gap-1 px-1.5 text-[10px] font-medium"
          >
            <TreeStructureIcon className="size-3" />
            <T context="badge counting nested subtasks on a generated stage">
              <Num>{node.children.length}</Num> nested
            </T>
          </Badge>
        ) : null}
        <IntendedAssigneeSelect
          value={node.intendedAssignee}
          disabled={disabled}
          ariaLabel={gt('Designed for {key}', {
            context: 'accessible label for generated stage designed-for picker',
            key: node.key || node.name,
          })}
          onChange={(intendedAssignee) => patch({ intendedAssignee })}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          disabled={disabled}
          aria-label={gt('Remove stage', {
            context: 'icon button removing a generated stage',
          })}
          onClick={() => onChange(removeDraftNode(nodes, node.clientId))}
        >
          <TrashIcon className="size-3.5" />
        </Button>
      </div>
      {reused ? <StageDraftReuseHint reused={reused} /> : null}
    </div>
  );
}

function StageDraftNestedSection({
  node,
  depth,
  stageOptions,
  parentKey,
  issues,
  disabled,
  onChange,
  nodes,
  addSubtaskButton,
}: {
  node: StageTreeDraftNode;
  depth: number;
  stageOptions: readonly TaskTypeStageOption[];
  parentKey: string;
  issues: StageDraftIssue[];
  disabled?: boolean;
  onChange: (next: StageTreeDraftNode[]) => void;
  nodes: StageTreeDraftNode[];
  addSubtaskButton: ReactNode;
}) {
  if (node.children.length > 0) {
    return (
      <StageHierarchyBranch nestedCount={node.children.length}>
        {node.children.map((child, childIndex) => (
          <StageDraftRow
            key={child.clientId}
            node={child}
            index={childIndex}
            siblingCount={node.children.length}
            depth={depth + 1}
            stageOptions={stageOptions}
            parentKey={parentKey}
            issues={issues}
            disabled={disabled}
            onChange={onChange}
            nodes={nodes}
          />
        ))}
        {addSubtaskButton}
      </StageHierarchyBranch>
    );
  }
  if (!addSubtaskButton) return null;
  return <div className="ml-4 pl-4">{addSubtaskButton}</div>;
}

function StageDraftRow({
  node,
  index,
  siblingCount,
  depth,
  stageOptions,
  parentKey,
  issues,
  disabled,
  onChange,
  nodes,
}: {
  node: StageTreeDraftNode;
  index: number;
  siblingCount: number;
  depth: number;
  stageOptions: readonly TaskTypeStageOption[];
  parentKey: string;
  issues: StageDraftIssue[];
  disabled?: boolean;
  onChange: (next: StageTreeDraftNode[]) => void;
  nodes: StageTreeDraftNode[];
}) {
  const issue = issues.find((item) => item.clientId === node.clientId);
  const reused = reusedExistingType(node, stageOptions, parentKey);
  const canNest = depth < MAX_TASK_TYPE_STAGE_TREE_DEPTH;
  const isNested = depth > 1;
  const addSubtaskButton = canNest ? (
    <AddSubtaskButton
      disabled={disabled}
      onClick={() =>
        onChange(addChildDraftNode(nodes, node.clientId, newStageDraftNode()))
      }
    />
  ) : null;
  const card = (
    <StageDraftCard
      node={node}
      index={index}
      siblingCount={siblingCount}
      issue={issue}
      reused={reused}
      disabled={disabled}
      isNested={isNested}
      onChange={onChange}
      nodes={nodes}
    />
  );

  return (
    <div className="flex flex-col gap-1.5">
      {isNested ? <NestedStageConnector>{card}</NestedStageConnector> : card}
      <StageDraftNestedSection
        node={node}
        depth={depth}
        stageOptions={stageOptions}
        parentKey={parentKey}
        issues={issues}
        disabled={disabled}
        onChange={onChange}
        nodes={nodes}
        addSubtaskButton={addSubtaskButton}
      />
    </div>
  );
}
